import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { AlertCircle, RefreshCw, Eye } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

// ============================================================================
// Types
// ============================================================================

interface HITLProposal {
  id: string;
  site_id: string;
  site_name: string | null;
  sector_id: number;
  parent_category_path: string;
  verdict: string;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

const VERDICT_META: Record<string, { label: string; cls: string }> = {
  needs_hitl:          { label: 'Needs HITL',         cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  split_recommended:   { label: 'Split recommended',  cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  split_required:      { label: 'Split required',     cls: 'bg-red-100 text-red-800 border-red-300' },
  no_split:            { label: 'No split',           cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

// ============================================================================
// Helpers
// ============================================================================

function VerdictBadge({ verdict }: { verdict: string }) {
  const meta = VERDICT_META[verdict] ?? { label: verdict, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString();
}

// ============================================================================
// Page
// ============================================================================

export default function CategoryGranularityHITL() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<HITLProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/category-granularity/hitl-proposals');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      setProposals(data.proposals ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Category Split — HITL Review</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Proposals awaiting human review before a category split can proceed.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Pending proposals
            {!loading && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({proposals.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : proposals.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No proposals awaiting HITL review.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Site</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Verdict</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {proposals.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      {p.site_name ?? p.site_id}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs truncate" title={p.parent_category_path}>
                      {p.parent_category_path}
                    </TableCell>
                    <TableCell>
                      <VerdictBadge verdict={p.verdict} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate" title={p.reason ?? ''}>
                      {p.reason ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {fmtDate(p.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/catalog/category-splits/${p.id}`)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
